import { hasUnsentWork, subscribeUnsent, unsentWorkWords } from "../features/common/form/unsentWork";

const LOGIN_FIRST_CONTROL_RELOAD_KEY = "soc.pwa.login.first-control-reload";

/** Fired when a new worker's reload is held for unsent work; detail: what is waiting, in words. */
export const RELOAD_WAITING_EVENT = "soc:reload-waiting";

/**
 * THE WORKER'S RELOAD WAITS FOR UNSENT WORK (forms plan §2.2, D8, C6).
 *
 * A replacement worker reloads the tab, and nothing used to check whether an
 * operator was half-way through a policy edit or a reason: the reload threw
 * the work away. Now the reload is held while anything is unsent (the
 * registry in common/form/unsentWork.ts, the DOM's draft marks, a reason typed
 * in an open dialog) and happens by itself once it is sent or discarded. The
 * version toast says what it waits for and offers [Reload anyway].
 */
export function createReloadGate(reload: () => void = () => window.location.reload()) {
  let waiting = false;
  let done = false;
  let stop: (() => void) | null = null;
  const fire = () => {
    done = true;
    stop?.();
    reload();
  };
  const check = () => {
    if (waiting && !done && hasUnsentWork().length === 0) fire();
  };
  return {
    /** The worker changed: reload now, or hold the reload until nothing would be lost. */
    request(): "reloaded" | "waiting" | "done" {
      if (done) return "done";
      if (hasUnsentWork().length === 0) {
        fire();
        return "reloaded";
      }
      if (!waiting) {
        waiting = true;
        const unsubscribe = subscribeUnsent(check);
        // A draft marked only in the DOM announces nothing when it is saved.
        const timer = window.setInterval(check, 2000);
        stop = () => {
          unsubscribe();
          window.clearInterval(timer);
        };
        window.dispatchEvent(new CustomEvent(RELOAD_WAITING_EVENT, { detail: unsentWorkWords() }));
      }
      return "waiting";
    },
    waiting: () => waiting && !done,
    /** The operator's own choice, naming what it loses: reload now. */
    reloadAnyway: () => {
      if (!done) fire();
    }
  };
}

let gate: ReturnType<typeof createReloadGate> | null = null;

/** Whether a new console's reload is being held for unsent work. */
export function reloadWaiting(): boolean {
  return gate?.waiting() ?? false;
}

/** Reload now, whatever is unsent: the version toast's [Reload anyway]. */
export function reloadAnyway(): void {
  if (gate) gate.reloadAnyway();
  else window.location.reload();
}

function isStandaloneDisplay(): boolean {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return Boolean(nav.standalone || window.matchMedia?.("(display-mode: standalone)").matches);
}

function isChromiumInstallBrowser(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const iOS = /ipad|iphone|ipod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  if (iOS) return false;
  return /chrome|chromium|crios|edg|edga|edgios/.test(ua) && !/firefox|fxios/.test(ua);
}

function markLoginFirstControlReload(): boolean {
  try {
    if (window.sessionStorage.getItem(LOGIN_FIRST_CONTROL_RELOAD_KEY) === "1") return false;
    window.sessionStorage.setItem(LOGIN_FIRST_CONTROL_RELOAD_KEY, "1");
    return true;
  } catch {
    return false;
  }
}

function clearLoginFirstControlReload(): void {
  try {
    window.sessionStorage.removeItem(LOGIN_FIRST_CONTROL_RELOAD_KEY);
  } catch {
    // Ignore blocked storage. It only disables the reload guard reset.
  }
}

function reloadLoginAfterFirstControlReady(): void {
  if (window.location.pathname !== "/login") return;
  if (!isChromiumInstallBrowser() || isStandaloneDisplay()) return;

  navigator.serviceWorker.ready
    .then(() => {
      if (navigator.serviceWorker.controller) {
        clearLoginFirstControlReload();
        return;
      }
      if (!markLoginFirstControlReload()) return;
      window.location.reload();
    })
    .catch(() => {
      /* offline support is non-critical; ignore registration failures */
    });
}

// Registers the service worker emitted by vite-plugin-pwa. The worker is
// generated only by `vite build`, so we skip registration in dev (it would
// 404) and in test. Registration is best-effort: failures never block the app.
export function registerServiceWorker(): void {
  if (!import.meta.env.PROD) return;
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;

  // When a replacement worker takes control, reload once so the page runs
  // against the fresh worker. Do not reload on first install: that can race
  // with the login install button and erase the operator's click.
  const hadController = Boolean(navigator.serviceWorker.controller);
  gate = createReloadGate();
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // The login route has an install button; update reloads can consume the
    // trusted click that Chromium requires for the native install prompt.
    if (window.location.pathname === "/login") return;
    if (!hadController) return;
    // Once, and never under unsent work: the gate holds it until that is
    // sent or discarded, or the operator chooses [Reload anyway].
    gate?.request();
  });

  const register = () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((registration) => {
        if (!registration) return;
        watchForUpdates(registration);
      })
      .catch(() => {
        /* offline support is non-critical; ignore registration failures */
      });
  };

  register();
  reloadLoginAfterFirstControlReady();
}

/** How often an open tab asks whether a newer worker has been published. */
const UPDATE_INTERVAL_MS = 60 * 60 * 1000;

/**
 * ASK FOR A NEW WORKER WHILE THE TAB IS OPEN.
 *
 * A browser checks for an updated service worker on NAVIGATION. This console is
 * a monitoring surface: an analyst opens it at the start of a shift and does not
 * navigate again, so a tab left open across a deploy kept running the worker —
 * and the precached shell — it started with, for as long as the tab lived. That
 * is the same failure mode as the blank-console incident, one step earlier:
 * there, an old worker served a stale shell after a deploy; here, an old worker
 * is simply never asked to step aside.
 *
 * Two triggers, because neither alone covers a monitoring tab:
 *   - an hourly poll, for a tab nobody touches;
 *   - a check when the tab is brought back to the foreground, which is what
 *     actually happens when an operator returns to the console after a deploy.
 *
 * The check itself is cheap: `/sw.js` is served `no-cache`, so this is one
 * conditional request that answers 304 when nothing has changed. The reload is
 * still governed by the controllerchange handler above — this only makes sure
 * the question gets asked.
 */
function watchForUpdates(registration: ServiceWorkerRegistration): void {
  const check = () => {
    // A failed update check is not worth surfacing: the tab keeps working on
    // the worker it has, which is the same outcome as never having asked.
    registration.update().catch(() => {});
  };

  window.setInterval(check, UPDATE_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") check();
  });
}
